/*
  bf-training-screens-b.js
  Videos, Coaching, Shop, Membership and Management routes.

  The pattern throughout: build the whole interface, then disable only the
  action whose dependency is missing, and say which dependency it is. A screen
  that renders nothing teaches the member nothing about what is coming.
*/
(function () {
  'use strict';
  var A = window.BFTrainingApp;
  if (!A) return;
  var H = A.helpers;
  var esc = H.esc, btn = H.btn, tag = H.tag, panel = H.panel, row = H.row,
      head = H.head, note = H.note, empty = H.empty, money = H.money,
      api = H.api, load = H.load, S = H.state, CFG = H.config;

  /* ================= VIDEOS =========================================== */

  A.register('videos', async function () {
    var pr = await load('program', '/program', true);
    if (S.programStatus === 403) return A.__locked();
    var stations = (pr && pr.stations) || [];
    /* "WATCH" IS A PROMISE. ONLY MAKE IT WHERE THERE IS FOOTAGE.
       This screen was titled "Workout videos", headed "Watch the setup, then
       do it", said demonstrations were free to watch — and then listed five
       exercises each reading "Not filmed yet" under a fully enabled "Watch"
       button. The destination was honest ("No footage has been filmed for this
       exercise yet. Read the setup before your first set."); the entrance sold
       something that does not exist. The written guide is real and useful, so
       the fix is to name it, not to hide the section.
       Keyed to the footage that is actually there: the moment a reviewed video
       is attached, that row says Watch again and the collection renames
       itself. The backlog is untouched. */
    var filmed = stations.filter(function (st) { return !!st.video; }).length;
    return (filmed
      ? head('WORKOUT VIDEOS', 'Watch the setup, then do it.',
          'Demonstrations are free to watch. The written program is the paid part.', '', 'then do it')
      : head('EXERCISE GUIDES', 'Read the setup, then do it.',
          'Written setup for every exercise, free to read. No footage is filmed yet.', '', 'then do it')) +
      (stations.length
        ? '<div class="bf-grid three">' + stations.map(function (st) {
            return '<div class="bf-panel"><h3 style="margin:0 0 6px">' + esc(st.exerciseName) + '</h3>' +
              '<p class="bf-sub" style="font-size:13px">' +
              (st.video ? 'Setup and movement.' : 'Written setup. Not filmed yet.') + '</p>' +
              '<div class="bf-actions">' +
              btn(st.video ? 'Watch →' : 'Read setup →',
                  'player/' + encodeURIComponent(st.id), 'ghost small') +
              '</div></div>';
          }).join('') + '</div>'
        : panel('', A.__incompatibleNote(pr) ||
            empty('Reviewed footage has not been filmed and approved yet. Nothing is hidden behind a paywall — it does not exist yet.')));
  });

  A.__locked = function () {
    return head('MEMBERS ONLY', 'This is part of the paid program.',
      'Demonstrations stay free to watch. The written setup, the prescription and approved alternatives are what a membership buys.') +
      '<div class="bf-split">' +
        panel('What you get', row('The written program', 'Setup steps, sets, reps and rest.') +
          row('Your progress', 'Weeks banked, credits, milestones.') +
          row('Text check-ins', 'Reply and it is logged.')) +
        panel('', '<div class="bf-actions">' +
          btn('See the program', null, 'primary', 'data-href="' + esc(CFG.landingUrl || '/pages/bullfit-training') + '"') +
          '</div>') + '</div>';
  };
  A.register('locked', async function () { return A.__locked(); });

  /* THE PLAYER, FOR ONE EXERCISE.
     Which exercise comes from the route (`#player/<stationId>`) and falls back
     to the one the member is currently on, so "Help with this exercise" opens
     the right footage rather than always the first station.

     The embed matches the storefront's Gym Mode section exactly:
     youtube-nocookie so tracking cookies wait until play, playsinline so a
     phone does not hijack the screen, and allowfullscreen so it still can when
     the member asks. The backend hands over a validated id or nothing — there
     is no raw string here to sanitise. */
  function videoMarkup(st) {
    var v = st && st.video;
    if (v && v.kind === 'youtube') {
      return '<div class="bf-embed">' +
        '<iframe src="https://www.youtube-nocookie.com/embed/' + esc(v.id) + '?rel=0&playsinline=1"' +
        ' title="' + esc(st.exerciseName) + ' setup"' +
        ' loading="lazy" referrerpolicy="strict-origin-when-cross-origin"' +
        ' allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"' +
        ' allowfullscreen></iframe></div>' +
        '<p class="bf-note">Demonstrations are free to watch. ' +
        '<a href="' + esc(v.watchUrl) + '" target="_blank" rel="noopener">Watch on YouTube</a>.</p>';
    }
    if (v && v.kind === 'file') {
      return '<div class="bf-embed"><video class="bf-video" controls playsinline ' +
        'preload="metadata" src="' + esc(v.url) + '"></video></div>';
    }
    /* "Not filmed yet" and "configured but unusable" are different facts and
       the member is told which. One is our backlog; the other is a broken
       value someone can go and fix. */
    return '<div class="bf-embed bf-embed--empty">' +
      '<p class="bf-empty">' + esc(st && st.videoIssue === 'unrecognised_link'
        ? 'The video link saved for this exercise is not one we can play, so nothing is shown rather than a player that fails.'
        : 'Footage for this exercise has not been filmed and approved yet.') + '</p></div>';
  }
  A.__videoMarkup = videoMarkup;

  /* A RANGE IS NOT A NUMBER, AND A HOLD IS NEITHER.
     This screen printed `st.reps`, so a prescription written as 8-15 showed
     as 8 and a 20-45 second plank showed as "6". The member reads this page
     before their first set — it is the one place the prescription has to be
     exactly what was written. */
  function playerReps(st) {
    if (st.repsText) return st.repsText;
    if (st.repsRange) return st.repsRange.min + '\u2013' + st.repsRange.max;
    return st.reps == null ? '' : String(st.reps);
  }

  A.register('player', async function () {
    var pr = await load('program', '/program', true);
    if (S.programStatus === 403) return A.__locked();
    var stations = (pr && pr.stations) || [];
    /* The station asked for, else the one they are on, else the first. */
    var wanted = (A.helpers.routeParam && A.helpers.routeParam()) || S.playerStation || null;
    var st = null;
    for (var i = 0; i < stations.length; i++) {
      if (String(stations[i].id) === String(wanted)) { st = stations[i]; break; }
    }
    if (!st) st = stations[0];
    if (!st) {
      return head('VIDEO LESSON', 'Nothing to watch yet.',
        'Sessions appear here once the program content is approved.') +
        panel('', empty('No exercises are released for your plan yet.'));
    }

    /* Only call it a video lesson when there is a video. With none filmed, the
       heading promised a film and the invitation told the member to watch it
       once before their first set — above a panel explaining that no footage
       exists. The written guide is what they actually get, so that is what it
       is called. */
    var filmed = !!st.video;
    return head(filmed ? 'VIDEO LESSON' : 'WRITTEN EXERCISE GUIDE', st.exerciseName,
      filmed ? 'Watch it once before your first set.'
             : 'No footage has been filmed for this exercise yet. Read the setup before your first set.',
      st.draftPreview ? tag('Draft', 'yellow') : '') +
      '<div class="bf-split"><div class="bf-stack">' +
        panel('', videoMarkup(st)) +
        panel('Setup', (st.setupSteps || []).map(function (step, n) {
          return row(String(n + 1).padStart(2, '0'), step);
        }).join('') || empty('Setup steps appear once this exercise is approved.')) +
      '</div><div class="bf-stack">' +
        panel('This exercise', row('Prescription',
            (st.sets != null ? st.sets + ' \u00d7 ' + playerReps(st) : 'Not set') +
            (st.rest ? ' \u00b7 ' + st.rest + ' rest' : '')) +
          (st.tempo ? row('Tempo', st.tempo) : '') +
          (st.commonMistake ? row('Common mistake', st.commonMistake) : '') +
          (st.harderSteps && st.harderSteps.length
            ? row('To make it harder', st.harderSteps.join(' \u2192 ')) : '') +
          (st.easier && st.easier.length
            ? row('If it is too hard', st.easier.join(' \u00b7 ')) : '') +
          (st.standingOption
            ? row('If you cannot get on the floor',
                  st.standingOption.name + '. ' + st.standingOption.steps) : '') +
          row('Reviewed', st.reviewStatus === 'reviewed'
            ? 'Yes, by ' + esc(st.reviewer || 'a reviewer') : 'Not yet')) +
        /* Back to the workout, which resumes at the saved position — watching
           a demonstration is part of doing the session, not leaving it. */
        panel('', '<div class="bf-actions">' + btn('Back to my workout', 'workout', 'primary') +
          /* The collection is named for what it holds. While nothing is
             filmed it holds written guides, and a button promising videos
             sends the member back to the same written guides. */
          btn('Exercise guides', 'videos', 'ghost') + '</div>') +
      '</div></div>';
  });

  /* ================= COACHING ========================================= */

  A.register('guides', async function () {
    return head('YOUR BULLFIT CREW', 'A little direction goes a long way.',
      'Both trainers are automated. One free introductory consultation is included.', '', 'a long way') +
      '<div class="bf-grid two">' +
        '<div class="bf-panel"><div class="bf-eyebrow">Todd</div>' +
          '<h3 style="margin:8px 0">Product and ingredient education</h3>' + tag('AI trainer', 'cyan') +
          '<p class="bf-sub" style="margin-top:10px">Grounded in approved product material. Not medical advice.</p>' +
          '<div class="bf-actions">' + btn('Meet Todd', 'todd', 'ghost') + '</div></div>' +
        '<div class="bf-panel"><div class="bf-eyebrow">Sophie</div>' +
          '<h3 style="margin:8px 0">Planning, encouragement and accountability</h3>' + tag('AI trainer', 'cyan') +
          '<p class="bf-sub" style="margin-top:10px">Helps you plan the week. Training technique comes from the reviewed program.</p>' +
          '<div class="bf-actions">' + btn('Meet Sophie', 'sophie', 'ghost') + '</div></div>' +
      '</div>' +
      panel('Talk to a person', row('Free introductory consultation', 'Included once with your membership.',
        btn('Find a time', 'freecall', 'small cyan')) +
        row('Follow-up consultation', 'Booked at the published rate.', btn('See availability', 'paidcall', 'small ghost'))) +
      note('All chats are automated. One free introductory consultation is included. Future live calls are paid and subject to availability.');
  });

  function chatScreen(guide, title, blurb) {
    return async function () {
      return head(guide === 'todd' ? 'CHAT WITH TODD AI' : 'CHAT WITH SOPHIE AI', title, blurb,
        tag('Automated', 'cyan')) +
        '<div class="bf-split"><div class="bf-stack">' +
          panel('', '<div data-chat-log style="display:grid;gap:12px;min-height:120px"></div>' +
            '<div class="bf-form" style="margin-top:14px"><label>Your message' +
            '<textarea rows="3" data-chat-input placeholder="Ask a question"></textarea></label></div>' +
            '<div class="bf-actions"><button class="bf-btn cyan" data-chat-send data-guide="' + guide + '">Send</button></div>' +
            '<p class="bf-note" data-chat-msg role="status" aria-live="polite"></p>') +
        '</div><div class="bf-stack">' +
          panel('What this is', row('Automated', 'Replies are generated. No person reads this.') +
            row('Grounded', 'Answers come from approved material, or say they do not have it.') +
            (guide === 'todd'
              ? row('Not medical advice', 'Pharmacist-reviewed product material is not a training or medical qualification.')
              : row('Not technique advice', 'Setup and technique come from the reviewed program.'))) +
          panel('Rather talk to a person?', '<div class="bf-actions">' + btn('Book a consultation', 'freecall', 'ghost small') + '</div>') +
        '</div></div>';
    };
  }
  A.register('todd', chatScreen('todd', 'Todd AI', 'Product and ingredient questions, answered from approved material.'));
  A.register('sophie', chatScreen('sophie', 'Sophie AI', 'Planning your week, finding your way around, and encouragement.'));

  async function consultScreen(paid) {
    var c = await load('consultations', '/consultations', true);
    if (!c) return head('CONSULTATIONS', 'Unavailable.', '') + panel('', empty('Try again shortly.'));
    var missing = c.missingConfiguration || [];
    var canBook = paid ? c.paidBookingAvailable : c.available;
    var price = c.followUpPrice ? money(c.followUpPrice.minor, c.followUpPrice.currency) : null;

    var why = !c.available
      ? 'Booking is not switched on yet: ' + missing.join(', ') + ' still to be configured.'
      : (paid && !c.paidBookingAvailable)
        ? 'A follow-up price has not been published yet, so paid booking stays closed.'
        : null;

    return head(paid ? 'PAID FOLLOW-UP CALL' : 'FREE INTRODUCTORY CALL',
      paid ? 'Book a follow-up.' : 'Your first call is included.',
      paid ? 'Booked at the published rate, once availability is set.'
           : 'One free introductory consultation comes with your membership.',
      paid ? (price ? tag(price, 'cyan') : tag('Price not published', 'yellow'))
           : tag(c.freeIntro && c.freeIntro.used ? 'Already used' : 'Included', c.freeIntro && c.freeIntro.used ? '' : 'cyan')) +
      '<div class="bf-split">' +
        panel('Choose a time',
          why ? note(why, true) : '<div class="bf-form">' +
            '<label>Preferred time<input type="datetime-local" data-consult-when></label></div>' +
            '<div class="bf-actions"><button class="bf-btn cyan" data-consult-book data-paid="' + (paid ? '1' : '') + '"' +
            (canBook ? '' : ' disabled') + '>Request this time</button></div>' +
            note('A time is only confirmed once the booking provider confirms it. Nothing here shows a confirmed appointment before that.') +
            '<p class="bf-note" data-consult-msg role="status" aria-live="polite"></p>') +
        panel('Your bookings', (c.bookings && c.bookings.length)
          ? c.bookings.map(function (b) {
              return row(new Date(b.starts_at).toLocaleString(), b.guide,
                tag(b.state, b.state === 'confirmed' ? 'cyan' : b.state === 'failed' ? 'pink' : ''));
            }).join('')
          : empty('Nothing booked yet.')) +
      '</div>';
  }
  A.register('freecall', function () { return consultScreen(false); });
  A.register('paidcall', function () { return consultScreen(true); });

  A.register('booking', async function () {
    var c = await load('consultations', '/consultations', true);
    var bookings = (c && c.bookings) || [];
    var latest = bookings[0];
    return head('CONSULTATION', latest ? 'Your booking' : 'No booking yet',
      'A booking shows as confirmed only after the provider confirms it.') +
      panel('', latest
        ? row(new Date(latest.starts_at).toLocaleString(), latest.guide + ' · ' + latest.duration_min + ' min',
            tag(latest.state, latest.state === 'confirmed' ? 'cyan' : ''))
          + (latest.state === 'pending' ? note('Waiting on the provider. This is not a confirmed appointment yet.', true) : '')
          + (latest.state === 'failed' ? note('The provider did not accept this time. Nothing was charged and your free call is still available.', true) : '')
        : empty('Book a call and it appears here.')) +
      '<div class="bf-actions">' + btn('Book a call', 'freecall', 'ghost') + '</div>';
  });

  /* ================= SHOP ============================================= */

  /* LIVE PRODUCT DATA, FETCHED AT RENDER TIME.
     The backend decides WHAT to suggest and WHY, from explicit answers only.
     It deliberately caches no price, variant or stock state, because a price
     we cached is a price we are wrong about. Those come from Shopify's own
     product endpoint here, so what the member reads is what the shop says. */
  async function liveProduct(handle) {
    try {
      var r = await fetch('/products/' + encodeURIComponent(handle) + '.js',
                          { headers: { accept: 'application/json' } });
      if (!r.ok) return null;
      return await r.json();
    } catch (e) { return null; }
  }

  function variantPicker(p) {
    var sellable = (p.variants || []).filter(function (v) { return v.available; });
    if (sellable.length === 0) return '';
    if (sellable.length === 1) {
      return '<input type="hidden" data-variant-for="' + esc(p.handle) + '" value="' + esc(sellable[0].id) + '">';
    }
    /* Only variants that can actually be bought are listed. Offering a sold-out
       size and refusing it on submit wastes the member's time. */
    return '<label class="bf-sub" style="display:block;font-size:12px;margin:8px 0 4px">Choose</label>' +
      '<select class="bf-select" data-variant-for="' + esc(p.handle) + '">' +
      sellable.map(function (v) {
        return '<option value="' + esc(v.id) + '">' + esc(v.title) + ' — ' + money(v.price) + '</option>';
      }).join('') + '</select>';
  }

  function productCard(sug, p) {
    if (!p) {
      return '<div class="bf-panel"><h3 style="margin:0 0 6px">' + esc(sug.handle.replace(/-/g, ' ')) + '</h3>' +
        empty('This product could not be loaded from the shop just now.') + '</div>';
    }
    var any = (p.variants || []).some(function (v) { return v.available; });
    var img = (p.featured_image || (p.images || [])[0]);
    return '<div class="bf-panel">' +
      (img ? '<img src="' + esc(img) + '&width=600" alt="" loading="lazy" ' +
             'style="width:100%;border-radius:8px;margin-bottom:10px">' : '') +
      '<h3 style="margin:0 0 4px">' + esc(p.title.split('|')[0].trim()) + '</h3>' +
      '<p style="color:var(--bf-cyan);font-family:var(--bf-display,Anton);font-size:20px;margin:0 0 8px">' +
        money(p.price) + '</p>' +
      (sug.because ? '<p class="bf-sub" style="font-size:13px">' + esc(sug.because) + '</p>' : '') +
      /* "Matched on your X" is only ever printed for a SPECIFIC claim, where
         a saved answer and a product rule together establish the need. A
         category item says what put it on screen and nothing more — gym
         access does not establish that this person needs a hundred-dollar
         belt, and wording it as a match would say that it does. */
      (sug.claim === 'specific' && sug.basis && sug.basis.length
        ? '<p class="bf-note" style="font-size:12px">Matched on your ' + esc(sug.basis.join(' and ')) + '.</p>'
        : '') +
      (any ? variantPicker(p) : '') +
      '<div class="bf-actions">' +
        (any
          ? '<button class="bf-btn cyan small" data-add-handle="' + esc(p.handle) + '">Add to cart</button>'
          : tag('Out of stock', '')) +
        '<a class="bf-btn ghost small" href="/products/' + esc(p.handle) + '" ' +
          'target="_blank" rel="noopener">View details</a>' +
      '</div></div>';
  }

  A.register('product', async function () {
    var rec = await api('/recommendations');
    var data = (rec.ok && rec.data) || { specific: [], optionalGear: [], alsoAvailable: [], unanswered: [] };
    var all = (data.specific || []).concat(data.optionalGear || [], data.alsoAvailable || []);

    /* One round of fetches, in parallel, so a page of suggestions is not a
       page of sequential requests. */
    var live = {};
    await Promise.all(all.map(async function (sug) { live[sug.handle] = await liveProduct(sug.handle); }));

    var card = function (s) { return productCard(s, live[s.handle]); };
    var specificCards = (data.specific || []).map(card).join('');
    var gearCards = (data.optionalGear || []).map(card).join('');
    var otherCards = (data.alsoAvailable || []).map(card).join('');

    return head('BULLFIT PRODUCTS', 'Optional, always.',
      'None of this is required. Your program, your logging and your progress work the same without any of it.',
      '', 'Optional') +
      (specificCards
        ? panel('Based on what you told us',
            '<p class="bf-sub">Each of these follows from a specific answer you gave.</p>' +
            '<div class="bf-grid three">' + specificCards + '</div>')
        : '') +
      (gearCards
        ? panel('Optional gym gear',
            /* Deliberately not "recommended for you". Where you train says
               which CATEGORY is relevant; it does not establish that you need
               any particular item in it. */
            '<p class="bf-sub">Things people training the way you described often use. ' +
            'None of it is matched to you personally, and no session in your program asks for any of it.</p>' +
            '<div class="bf-grid three">' + gearCards + '</div>')
        : '') +
      (otherCards
        ? panel('Also available',
            '<p class="bf-sub">Not matched to your answers — just what else BullFit makes.</p>' +
            '<div class="bf-grid three">' + otherCards + '</div>')
        : '') +
      (!specificCards && !gearCards && !otherCards
        ? panel('', empty(data.supplementsDeclined
            ? 'You have asked us not to suggest supplements. Nothing else matches your answers right now.'
            : 'Nothing matches your answers yet. Answering more of the plan questions can change that.'))
        : '') +
      '<p class="bf-note" data-cart-msg role="status" aria-live="polite"></p>' +
      panel('Suggestions',
        row('Supplement suggestions',
          data.supplementsDeclined ? 'Turned off' : 'On',
          data.supplementsDeclined
            ? '<button class="bf-btn ghost small" data-supplements="yes">Turn back on</button>'
            : '<button class="bf-btn ghost small" data-supplements="no">No supplements, thanks</button>') +
        row('Everything here is a one-time purchase',
          'Added to your normal BullFit cart. It is not part of your membership and does not renew.')) +
      note('Adding something here never changes your workout. Your logged sets, your place in the session and your rest timer are all waiting when you go back.');
  });

  A.register('cart', async function () {
    var cart = null;
    try { cart = await (await fetch('/cart.js')).json(); } catch (e) {}
    return head('YOUR CART', 'Everything in one basket.',
      'Training suggestions and anything else you are buying share the normal BullFit cart.', '', 'one basket') +
      panel('', cart && cart.items && cart.items.length
        ? cart.items.map(function (i) {
            return row(i.product_title, i.quantity + ' × ' + money(i.final_price, cart.currency),
              i.selling_plan_allocation ? tag('Subscription', 'pink') : tag('One-time', ''));
          }).join('')
        : empty('Your cart is empty.')) +
      '<div class="bf-actions">' +
        btn('View cart', null, 'primary', 'data-href="/cart"') +
        btn('Keep browsing', 'product', 'ghost') + '</div>';
  });

  /* ================= MEMBERSHIP ======================================= */

  A.register('account', async function () {
    var me = await load('me', '/me');
    return head('ACCOUNT', 'Your details.', 'Held by Shopify. Changing them changes them everywhere.', '', 'details') +
      '<div class="bf-split">' +
        panel('Account', row('Signed in as', (me && me.customerId) ? 'Customer ' + me.customerId : '—') +
          row('Timezone', (me && me.timezone) || 'UTC', tag('Used for your training day', ''))) +
        panel('Preferences', row('Text check-ins', 'Manage separately', btn('Open', 'sms', 'small ghost')) +
          row('Membership', 'Billing and renewal', btn('Open', 'membership', 'small ghost'))) +
      '</div>';
  });

  A.register('membership', async function () {
    var me = await load('me', '/me');
    var access = me && me.access;
    var active = access && access.hasAccess;
    return head('MEMBERSHIP', active ? 'You are a member.' : 'Not a member yet.',
      'Billing is handled by Shopify. Access follows the period you have paid for.',
      access ? tag(access.reason, active ? 'cyan' : '') : '') +
      '<div class="bf-split">' +
        panel('Status', row('Access', active ? 'Active' : 'Not active') +
          row('Reason', (access && access.reason) || '—') +
          (access && access.reason === 'comped'
            ? row('Preview', 'Comped account — no payment is associated with this access') : '')) +
        panel('Manage', (active
          ? row('Cancel renewal', 'You keep access to the end of the period you paid for.',
              btn('Cancel', 'cancel', 'small ghost'))
          : row('Join', 'See what a membership includes.',
              btn('See the program', null, 'small cyan', 'data-href="' + esc(CFG.landingUrl || '/pages/bullfit-training') + '"'))) +
          row('Billing history', 'Held in your Shopify account.',
            btn('Open account', null, 'small ghost', 'data-href="/account"'))) +
      '</div>';
  });

  A.register('cancel', async function () {
    return head('CANCELLATION', 'Cancelling stops the next renewal.',
      'It does not end your access today, and it does not remove what you have earned.', '', 'next renewal') +
      '<div class="bf-split">' +
        panel('What happens', row('Your access', 'Continues to the end of the period you have already paid for.') +
          row('Your earned gear', 'Stays yours. Cancelling never revokes it.') +
          row('Your history', 'Workouts, credits and banked weeks are kept.')) +
        panel('Cancel', note('Cancellation is handled through your Shopify account so it always reflects real billing.') +
          '<div class="bf-actions">' +
          btn('Manage subscription', null, 'ghost', 'data-href="/account"') +
          btn('Keep my membership', 'membership', 'primary') + '</div>') +
      '</div>';
  });

  /* ================= MANAGEMENT ======================================= */

  function adminUnavailable(what) {
    return panel('', empty(what + ' is not available. This account may not have staff access, or the data is still loading.'));
  }

  A.register('admin', async function () {
    var r = await api('/admin/overview');
    if (!r.ok) return head('OPERATIONS', 'Overview', '') + adminUnavailable('The overview');
    var c = r.data.counts, cfg = r.data.configuration, ct = r.data.content;
    var q = r.data.queue || null;
    function stat(label, v) {
      return '<div class="bf-panel"><div class="bf-eyebrow">' + esc(label) + '</div>' +
        '<p style="font-family:var(--bf-display,Anton);font-size:34px;margin:8px 0 0">' + esc(v) + '</p></div>';
    }
    /* WHAT WAS QUEUED AND NEVER SENT.
       kits_pending was in this response all along and no screen drew it, so
       the one number that would have revealed a stalled queue was served and
       discarded. Nothing drains outbox_job, so a non-zero figure here is work
       a member is owed and has not received — loud, not a quiet stat tile.
       An unreadable queue is NOT reported as zero: a reassuring nought is how
       this stayed invisible. */
    var pendingKits = c.kits_pending == null ? null : Number(c.kits_pending);
    var queued = q && q.readable ? Number(q.totalNotDone || 0) : null;
    var stalled = (pendingKits || 0) > 0 || (queued || 0) > 0;
    var queuePanel = panel('Queued work',
      (q && !q.readable
        ? note('The job queue could not be read. This is NOT the same as an empty ' +
               'queue — do not read it as nothing outstanding.', true)
        : '') +
      row('Kits owed, not yet sent',
        pendingKits == null ? 'unknown' : String(pendingKits),
        pendingKits ? tag('Action needed', 'yellow') : tag('Clear', 'cyan')) +
      (q && q.readable
        ? row('Jobs not done', String(queued), queued ? tag('Action needed', 'yellow') : tag('Clear', 'cyan')) +
          (q.oldest ? row('Oldest waiting since', String(q.oldest).slice(0, 10)) : '') +
          (q.notDone || []).map(function (x) {
            return row(x.kind + ' · ' + x.state, String(x.n));
          }).join('')
        : '') +
      (stalled
        ? note('Nothing currently processes this queue, so these will not send on ' +
               'their own. The entitlements behind them are recorded, so nothing is ' +
               'lost — but nothing ships until a worker runs.', true)
        : ''));
    return head('BULLFIT OPERATIONS', 'Overview', 'Live records from the training backend.') +
      '<div class="bf-grid four">' + stat('Members', c.members) + stat('Completions', c.completions) +
        stat('Banked weeks', c.banked_weeks) + stat('Kits issued', c.kits) + '</div>' +
      '<div style="margin-top:22px">' + queuePanel + '</div>' +
      '<div class="bf-split" style="margin-top:22px">' +
        panel('Content', row('Approved', String(ct.approved)) + row('Awaiting review', String(ct.awaitingReview),
          ct.awaitingReview ? tag('Action needed', 'yellow') : '')) +
        panel('Configuration',
          row('SMS provider', cfg.smsProviderName || 'not configured', tag(cfg.smsProvider ? 'On' : 'Off', cfg.smsProvider ? 'cyan' : '')) +
          row('SMS schema', cfg.smsSchema ? 'present' : 'missing', tag(cfg.smsSchema ? 'OK' : 'Pending', cfg.smsSchema ? 'cyan' : 'yellow')) +
          row('Guides', cfg.chatConfigured ? 'model configured' : 'no model', tag(cfg.chatConfigured ? 'On' : 'Off', cfg.chatConfigured ? 'cyan' : '')) +
          row('Consultations', (cfg.consultations && cfg.consultations.bookingAvailable) ? 'bookable' : 'not configured',
            tag((cfg.consultations && cfg.consultations.bookingAvailable) ? 'On' : 'Off', (cfg.consultations && cfg.consultations.bookingAvailable) ? 'cyan' : '')) +
          row('Reward fulfilment', cfg.rewardFulfilment ? 'on' : 'off') +
          row('Preview accounts', String(cfg.compedAccounts), tag('Excluded from rewards', 'yellow'))) +
      '</div>';
  });

  A.register('members', async function () {
    var r = await api('/admin/members');
    if (!r.ok) return head('MEMBERS', 'Member records', '') + adminUnavailable('The member list');
    var ms = r.data.members || [];
    return head('MEMBERS', 'Member records', 'Preview accounts are labelled so they are never read as revenue.') +
      panel('', ms.length ? ms.map(function (m) {
        return row('Customer ' + m.customerId,
          m.completions + ' workouts · ' + m.credits + ' credits · ' + m.bankedWeeks + ' weeks',
          (m.preview ? tag('Preview', 'yellow') + ' ' : '') +
          tag(m.access.reason, m.access.hasAccess ? 'cyan' : '') +
          (m.kitIssued ? ' ' + tag('Kit issued', '') : ''));
      }).join('') : empty('No members yet.'));
  });

  A.register('content', async function () {
    var r = await api('/admin/content');
    if (!r.ok) return head('PROGRAMS & VIDEOS', 'Publishing', '') + adminUnavailable('Content review');
    return head('PROGRAMS & VIDEOS', 'Publishing', 'Only approved stations reach members.') +
      (r.data.sessions || []).map(function (s) {
        return panel(s.name || s.key, s.stations.map(function (x) {
          return row(x.exerciseName, x.steps + ' steps · ' + x.alternatives + ' alternatives',
            tag(x.contentStatus, x.contentStatus === 'approved' ? 'cyan' : 'yellow') + ' ' +
            tag(x.reviewStatus, x.reviewStatus === 'reviewed' ? 'cyan' : ''));
        }).join(''));
      }).join('') +
      note('Content is edited in ' + (r.data.editedIn || 'the repository') + ' and approved by deploying that change, so every edit is reviewable and revertible.');
  });

  A.register('fulfillment', async function () {
    var r = await api('/admin/fulfilment');
    if (!r.ok) return head('KITS & REWARDS', 'Fulfilment', '') + adminUnavailable('The fulfilment queue');
    var jobs = r.data.jobs || [];
    return head('KITS & REWARDS', 'Fulfilment queue', 'One kit per member, ever.') +
      panel('', jobs.length ? jobs.map(function (j) {
        return row(j.kind === 'welcome_kit' ? 'Welcome kit' : 'Reward',
          'Customer ' + j.shopify_customer_id + ' · ' + String(j.created_at).slice(0, 10),
          tag(j.state, j.state === 'done' ? 'cyan' : '') + (j.last_error ? ' ' + tag('error', 'pink') : ''));
      }).join('') : empty('Nothing queued.'));
  });

  function adminStub(title, sub, body) {
    return async function () { return head('BULLFIT OPERATIONS', title, sub) + body(); };
  }

  A.register('equipmentadmin', async function () {
    // Built from the program content we already hold. Nothing external is
    // needed to answer "which equipment does the program require, and what
    // may a member use instead".
    var r = await api('/admin/content');
    if (!r.ok) return head('BULLFIT OPERATIONS', 'Equipment & alternatives', '') +
      adminUnavailable('Equipment');
    var families = {};
    (r.data.sessions || []).forEach(function (s2) {
      (s2.stations || []).forEach(function (st) {
        var fam = st.equipment || 'Unspecified';
        families[fam] = families[fam] || [];
        families[fam].push(st);
      });
    });
    var keys = Object.keys(families);
    return head('BULLFIT OPERATIONS', 'Equipment & alternatives',
      'Every station the program needs, and how many approved alternatives it has.') +
      (keys.length ? keys.map(function (fam) {
        return panel(fam, families[fam].map(function (st) {
          return row(st.exerciseName,
            st.alternatives + ' approved alternative' + (st.alternatives === 1 ? '' : 's'),
            (st.alternatives === 0 ? tag('None reviewed', 'yellow') : tag('Covered', 'cyan')) + ' ' +
            tag(st.contentStatus, st.contentStatus === 'approved' ? 'cyan' : ''));
        }).join(''));
      }).join('') : panel('', empty('No stations defined yet.'))) +
      note('A station with no reviewed alternative leaves a member stuck when the machine is taken. Those are the ones to fill first.');
  });

  A.register('messaging', async function () {
    var r = await api('/admin/overview');
    var cfg = r.ok ? r.data.configuration : null;
    return head('BULLFIT OPERATIONS', 'SMS operations', 'Sending stays off until a provider and consent are both ready.') +
      '<div class="bf-split">' +
        panel('Status', cfg
          ? row('Provider', cfg.smsProviderName || 'not configured', tag(cfg.smsProvider ? 'On' : 'Off', cfg.smsProvider ? 'cyan' : '')) +
            row('Schema', cfg.smsSchema ? 'present' : 'missing', tag(cfg.smsSchema ? 'OK' : 'Pending', cfg.smsSchema ? 'cyan' : 'yellow'))
          : empty('Unavailable.')) +
        panel('Rules', row('Consent', 'Stored with the disclosure shown, its version, source and time.') +
          row('Email list', 'Never treated as SMS consent.') +
          row('Replies', 'Matched to the workout the prompt asked about, not the clock.')) +
      '</div>';
  });

  A.register('calls', adminStub('Consultation operations',
    'Bookings, holds and provider state.', function () {
      return panel('', empty('Consultation operations appear once a booking provider is configured.')) +
        note('A booking is only ever shown as confirmed after the provider confirms it.');
    }));

  A.register('analytics', async function () {
    // Computed from the member records we already have. Retention here means
    // banked weeks, not logins — showing up is the thing being measured.
    var r = await api('/admin/members');
    if (!r.ok) return head('BULLFIT OPERATIONS', 'Retention', '') + adminUnavailable('Retention');
    var ms = (r.data.members || []);
    var real = ms.filter(function (m) { return !m.preview; });
    var paying = real.filter(function (m) { return m.access.hasAccess; });
    var trained = real.filter(function (m) { return m.completions > 0; });
    var banked = real.filter(function (m) { return m.bankedWeeks > 0; });
    var totalWeeks = real.reduce(function (a, m) { return a + m.bankedWeeks; }, 0);
    var totalWorkouts = real.reduce(function (a, m) { return a + m.completions; }, 0);
    function stat(label, v, sub) {
      return '<div class="bf-panel"><div class="bf-eyebrow">' + esc(label) + '</div>' +
        '<p style="font-family:var(--bf-display,Anton);font-size:32px;margin:8px 0 0">' + esc(v) + '</p>' +
        (sub ? '<p class="bf-sub" style="font-size:12px;margin:4px 0 0">' + esc(sub) + '</p>' : '') + '</div>';
    }
    return head('BULLFIT OPERATIONS', 'Retention',
      'Measured from banked weeks. Preview accounts are excluded so they cannot flatter the numbers.') +
      '<div class="bf-grid four">' +
        stat('Members', real.length, ms.length - real.length + ' preview excluded') +
        stat('With access', paying.length) +
        stat('Have trained', trained.length, trained.length + ' of ' + real.length) +
        stat('Banked a week', banked.length) +
      '</div>' +
      '<div class="bf-split" style="margin-top:22px">' +
        panel('Totals', row('Workouts reported', String(totalWorkouts)) +
          row('Weeks banked', String(totalWeeks)) +
          row('Average weeks per member', real.length ? (totalWeeks / real.length).toFixed(1) : '0')) +
        panel('What this does not tell you',
          row('Revenue', 'Billing lives in Shopify. Nothing here is money.') +
          row('Churn', 'Needs a cancellation history this backend does not keep yet.')) +
      '</div>';
  });

  A.register('settings', async function () {
    var r = await api('/admin/overview');
    var cfg = r.ok ? r.data.configuration : null;
    return head('BULLFIT OPERATIONS', 'Configuration', 'What is switched on, and what is still missing.') +
      panel('', cfg
        ? row('Comped preview accounts', String(cfg.compedAccounts), tag('Excluded from rewards', 'yellow')) +
          row('Staff accounts', String(cfg.staffAccounts)) +
          row('Reward fulfilment', cfg.rewardFulfilment ? 'on' : 'off') +
          row('Guides', cfg.chatConfigured ? 'model configured' : 'model missing') +
          row('Persona material', 'Todd ' + ((cfg.chatPersonas && cfg.chatPersonas.todd) ? 'supplied' : 'missing') +
            ' · Sophie ' + ((cfg.chatPersonas && cfg.chatPersonas.sophie) ? 'supplied' : 'missing')) +
          row('Consultations missing', (cfg.consultations && cfg.consultations.missing || []).join(', ') || 'nothing')
        : empty('Unavailable.')) +
      note('Values are set in the deployment environment, not here — a setting you can edit in two places drifts.');
  });

  A.register('flow', adminStub('Full journey & ownership',
    'Who owns each step of the member journey.', function () {
      return '<div class="bf-grid two">' +
        panel('Shopify', row('Storefront and checkout', 'Pages, cart, payment.') +
          row('Customer accounts', 'Sign-in and identity.') +
          row('Subscriptions', 'Appstle contracts and renewal.')) +
        panel('Training backend', row('Entitlement', 'Who may read the program.') +
          row('Credits and weeks', 'Exactly-once rules.') +
          row('Kit and rewards', 'One kit per member, ever.')) +
        '</div>';
    }));
})();
